import { useTranslate } from '@festina-life/ui/hooks'

import { TransferButton } from '../../components/transferButton/TransferButton'
import { useEnumId } from '../../hooks/useEnumId'
import { useNavigationParams } from '../../system/useNavigationParams'
import { createOperationName } from '../../uiModels/annulOperations/helpers'
import type { ConfiguredEnumLiteral } from '../../uiModels/enumLiterals/types'
import { detectEnumTransferConflict } from '../../uiModels/transfer/detectEnumTransferConflict'
import { useOperationsContext } from '../../workspace/operationsContext'

type Props = {
  literal: ConfiguredEnumLiteral
}

/** Transfers a single enum literal to an ancestor block. */
export const EnumLiteralTransferButton = ({ literal }: Props) => {
  const translate = useTranslate()
  const enumId = useEnumId()
  const { block: currentViewBlock } = useNavigationParams()
  const { configuredEnums } = useOperationsContext()

  const literalName = literal.internalOperation.entity?.name ?? ''

  const transferableLiterals =
    literal.block === currentViewBlock &&
    !literal.annulOperations.length &&
    !literal.isDraft
      ? [literal]
      : []

  return (
    <TransferButton
      item={{
        itemId: enumId,
        type: 'enumDefinition',
        operationName: `${enumId}.${literalName}`,
        versionCount: transferableLiterals.length,
        isTransferable: !!transferableLiterals.length,
      }}
      transfer={{
        label: translate('enumViewer.details.literal.actions.transfer'),
        description: translate(
          'transfer.confirmationModal.enumLiteral.description',
          { sourceBlock: currentViewBlock },
        ),
        getConflictWarning: (destinationBlock) => {
          const conflictType = detectEnumTransferConflict({
            configuredEnums,
            enumName: enumId,
            destinationBlock,
            transferringLiteralNames: [literalName],
          })

          return conflictType === 'literal' || conflictType === 'both'
            ? translate('transfer.confirmationModal.conflict.warning')
            : undefined
        },
        onConfirm: ({ transfer }) =>
          transfer({
            shouldInvalidateCacheAfterTransfer: true,
            operationName: createOperationName(literal.internalOperation),
            operations: transferableLiterals,
          }),
      }}
    />
  )
}
